// File: src/components/MarketSelectionModal.jsx
import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuCheck, LuMapPin, LuSearch, LuX } from 'react-icons/lu';

export default function MarketSelectionModal({
  open,
  onClose,
  markets = [],
  selectedMarketId = '',
  onSelect,
  loading = false,
  error = '',
  onRetry,
}) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  const [query, setQuery] = useState('');
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    const body = document.body;
    const meta = document.querySelector('meta[name="theme-color"]');
    const previousTheme = meta?.content;
    const previousFocus = document.activeElement;
    const alreadyLocked = body.classList.contains('overlay-active') || body.style.position === 'fixed';
    const y = window.scrollY || window.pageYOffset || 0;
    const previousStyle = {
      top: body.style.top,
      position: body.style.position,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    if (meta) meta.content = '#888888';
    if (!alreadyLocked) {
      body.classList.add('overlay-active');
      body.style.position = 'fixed';
      body.style.top = `-${y}px`;
      body.style.width = '100%';
      body.style.overflow = 'hidden';
    }
    dialogRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;
      const controls = [...(dialogRef.current?.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), [tabindex="0"]'
      ) || [])];
      const first = controls[0];
      const last = controls[controls.length - 1];
      const focus = document.activeElement;
      if (!first) { event.preventDefault(); dialogRef.current?.focus(); return; }
      if (event.shiftKey && (focus === first || !controls.includes(focus))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (focus === last || !controls.includes(focus))) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (meta) meta.content = previousTheme || '#ffffff';
      if (!alreadyLocked) {
        body.classList.remove('overlay-active');
        Object.assign(body.style, previousStyle);
        window.scrollTo(0, y);
      }
      if (previousFocus?.isConnected) previousFocus.focus?.({ preventScroll: true });
    };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;
  const search = query.trim().toLocaleLowerCase();
  const visibleMarkets = markets.filter((market) =>
    market.name.toLocaleLowerCase().includes(search)
  );
  const comingSoon = ['New York City', 'San Francisco', 'London']
    .filter((name) => !markets.some((market) => market.name.trim().toLowerCase() === name.toLowerCase()))
    .filter((name) => name.toLocaleLowerCase().includes(search));

  return createPortal(
    <>
      <div className="tribeMarketBackdrop" onClick={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}>
        <section ref={dialogRef} tabIndex={-1} className="tribeMarketSheet"
          role="dialog" aria-modal="true" aria-labelledby={titleId}>
          <header className="tribeMarketHeader">
            <h2 id={titleId}>Choose your city</h2>
            <button type="button" className="tribeMarketClose" aria-label="Close city selection" onClick={onClose}>
              <LuX aria-hidden="true" />
            </button>
          </header>
          <label className="tribeMarketSearch">
            <LuSearch aria-hidden="true" />
            <input type="search" aria-label="Search cities" placeholder="Search cities" value={query}
              onChange={(event) => setQuery(event.target.value)} />
          </label>
          <div className="tribeMarketList" aria-busy={loading}>
            <h3 className="tribeMarketSectionTitle">Available cities</h3>
            {loading ? <p className="tribeMarketMessage" role="status">Loading cities…</p>
              : error ? <div className="tribeMarketMessage" role="alert">
                <p>{error}</p>
                <button type="button" className="tribeMarketRetry" onClick={onRetry}>Try again</button>
              </div>
              : !markets.length ? <p className="tribeMarketMessage">New cities will appear here when gatherings are available.</p>
              : !visibleMarkets.length ? <p className="tribeMarketMessage" role="status">No available cities match your search.</p>
              : visibleMarkets.map((market) => {
                const selected = String(market._id) === String(selectedMarketId);
                return <button key={market._id} type="button" className="tribeMarketRow"
                  aria-pressed={selected} onClick={() => { onClose?.(); onSelect?.(String(market._id)); }}>
                  {selected && <LuMapPin aria-hidden="true" className="tribeMarketPin" />}
                  <span className="tribeMarketCityCopy">
                    <span className="tribeMarketName">{market.name}</span>
                    {market.name === 'Los Angeles' && <span className="tribeMarketRegion">California, USA</span>}
                  </span>
                  {selected && <LuCheck aria-hidden="true" className="tribeMarketCheck" />}
                </button>;
              })}
            {comingSoon.length > 0 && <>
              <h3 className="tribeMarketSectionTitle tribeMarketSoonTitle">Coming soon</h3>
              {comingSoon.map((name) => (
                <div key={name} className="tribeMarketRow tribeMarketSoonRow">
                  <span className="tribeMarketCityCopy">
                    <span className="tribeMarketName">{name}</span>
                    <span className="tribeMarketRegion">{name === 'New York City' ? 'New York, USA' : name === 'San Francisco' ? 'California, USA' : 'United Kingdom'}</span>
                  </span>
                  <span className="tribeMarketSoonBadge">Coming soon</span>
                </div>
              ))}
            </>}
          </div>
        </section>
      </div>
      <style>{`
        .tribeMarketBackdrop {
          position: fixed; inset: 0; z-index: 2147483000;
          display: flex; align-items: center; justify-content: center;
          box-sizing: border-box; padding: 20px;
          background: rgba(8,8,8,.48);
          backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }
        .tribeMarketSheet {
          width: 100%; max-width: 390px; max-height: 55vh; max-height: min(450px, 55dvh);
          display: flex; flex-direction: column; box-sizing: border-box;
          overflow: hidden; outline: none; color: #111; background: #fff;
          border: 1px solid rgba(17,17,17,.10); border-radius: 12px;
          box-shadow: 0 24px 80px rgba(0,0,0,.22), 0 4px 18px rgba(0,0,0,.08);
          animation: tribeMarketEnter 180ms ease-out;
        }
        .tribeMarketHeader {
          height: 48px; flex-shrink: 0; padding: 0 14px;
          display: flex; align-items: center; justify-content: space-between;
          border-bottom: 1px solid #eef0f2;
        }
        .tribeMarketHeader h2 { margin: 0; font-size: 14px; font-weight: 800; letter-spacing: -.01em; }
        .tribeMarketClose {
          width: 32px; height: 32px; padding: 0; border: 0; border-radius: 8px;
          display: inline-flex; align-items: center; justify-content: center;
          background: transparent; color: #111; font-size: 19px; cursor: pointer;
          -webkit-tap-highlight-color: transparent;
        }
        .tribeMarketIntro { flex-shrink: 0; margin: 0; padding: 18px 16px 0; color: #666; font-size: 13px; line-height: 1.5; }
        .tribeMarketSearch {
          display: flex; align-items: center; gap: 10px; margin: 14px 16px 6px;
          padding: 10px 12px; border: 1px solid #e5e7eb; border-radius: 8px;
          flex-shrink: 0;
        }
        .tribeMarketSearch svg { flex-shrink: 0; width: 18px; height: 18px; }
        .tribeMarketSearch input { width: 100%; min-width: 0; border: 0; outline: 0; background: transparent; font: inherit; font-size: 16px; color: #111; }
        .tribeMarketSearch:focus-within { border-color: #111; }
        .tribeMarketList { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; padding: 8px 16px 16px; }
        .tribeMarketRow {
          display: flex; align-items: center; gap: 12px; width: 100%; min-height: 60px;
          padding: 14px 0; border: 0; border-bottom: 1px solid #eef0f2;
          background: transparent; color: #111; font: inherit; text-align: left;
          cursor: pointer; -webkit-tap-highlight-color: transparent;
        }
        .tribeMarketRow:last-child { border-bottom: 0; }
        .tribeMarketPin { width: 18px; height: 18px; flex-shrink: 0; }
        .tribeMarketSectionTitle { margin: 10px 0 4px; font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: #777; }
        .tribeMarketSoonTitle { margin-top: 24px; }
        .tribeMarketCityCopy { display: flex; flex-direction: column; gap: 5px; flex: 1; min-width: 0; }
        .tribeMarketName { overflow-wrap: anywhere; font-size: 14px; font-weight: 600; }
        .tribeMarketRegion { font-size: 12px; line-height: 1.4; color: #777; }
        .tribeMarketSoonRow { cursor: default; color: #777; }
        .tribeMarketSoonBadge { font-size: 10px; line-height: 1.3; color: #888; border: 1px solid #e9e9e9; border-radius: 20px; padding: 5px 8px; flex-shrink: 0; }
        .tribeMarketCheck { width: 19px; height: 19px; flex-shrink: 0; }
        .tribeMarketMessage { margin: 0; padding: 24px 8px; color: #555; font-size: 14px; line-height: 1.5; text-align: center; }
        .tribeMarketMessage p { margin: 0 0 12px; }
        .tribeMarketRetry { border: 0; padding: 8px; background: transparent; color: #111; font: inherit; font-weight: 600; text-decoration: underline; cursor: pointer; }
        .tribeMarketRow:focus-visible, .tribeMarketClose:focus-visible, .tribeMarketRetry:focus-visible { outline: 2px solid #111; outline-offset: 2px; }
        @keyframes tribeMarketEnter { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @media (max-width: 520px) {
          .tribeMarketBackdrop { align-items: flex-end; padding: 10px 10px calc(10px + env(safe-area-inset-bottom, 0px)); }
          .tribeMarketSheet { max-width: none; }
        }
        @media (prefers-reduced-motion: reduce) { .tribeMarketSheet { animation: none; } }
      `}</style>
    </>, document.body
  );
}
